import { DateTimeFormatter, LocalDate, LocalTime, Instant, ZoneId } from '@js-joda/core';
import { isStringControl, rankWith } from '@jsonforms/core';
import type { ControlProps } from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Description, Label, Errors } from 'src/components/JsonForm/helpers';
import { JsonFormErrorFloatingPane } from 'src/components/JsonForm/JsonFormErrorFloatingPane';
import { fakeRequired, lightStyle } from 'src/components/JsonForm/ThinkalphaTheme';
import { Input } from 'src/components/ui';
import type { InputProps } from 'src/components/ui';
import { INSTANT_REMOVAL_FROM_ORDER_DATA_VALUE } from 'src/lib/orders/removePropsFromOrderData';

export const formatUpdateValue = (value: any, type: string) => {
    if (value === null || value === undefined || value === false) {
        return value;
    }

    switch (type) {
        case 'number': {
            value = String(value).trim();

            // Allow empty string to pass through
            if (value === '') {
                return INSTANT_REMOVAL_FROM_ORDER_DATA_VALUE;
            }

            const sanitized = value.replace(/_/g, '');
            const numberValue = Number(sanitized);
            const numberValueAsString = String(numberValue);

            // This preserves inputs like "0." or "0.00" during typing and returns a number
            // only when the values match, meaning the typed string is a valid number.
            if (Number.isFinite(numberValue) && sanitized === numberValueAsString) {
                return numberValue;
            }

            // Otherwise, keep as string to allow continued typing (e.g., ".", "-", "1.", "1.50")
            return value;
        }
        case 'integer':
            const v = String(value).trim();
            return Number.parseInt(v.replace(/_/g, ''), 10);
        case 'date':
            try {
                return LocalDate.parse(value.substring(0, 10)).format(DateTimeFormatter.ofPattern('yyyy-MM-dd'));
            } catch {
                return value;
            }
        case 'datetime-local':
            try {
                // Date without any time part
                if (value.length === 10) {
                    value = `${value}T00:00`;
                }
                return Instant.parse(`${value}:00Z`).toString();
            } catch {
                return value;
            }
        case 'time':
            try {
                return LocalTime.parse(value).format(DateTimeFormatter.ofPattern('HH:mm'));
            } catch {
                return value;
            }
        default:
            return value;
    }
};

export const formatData = (data: any, type: string, enabled: boolean) => {
    if (!enabled) {
        return '';
    }

    if (data === 0) {
        return 0;
    }

    if (!data) {
        return '';
    }

    switch (type) {
        case 'date': {
            // For some fields, we may want to display date picker only, but they are datetime-local in the schema
            const formatters = [
                DateTimeFormatter.ofPattern("yyyy-MM-dd'T'HH:mm"),
                DateTimeFormatter.ofPattern("yyyy-MM-dd'T'HH:mm:ss"),
                DateTimeFormatter.ofPattern("yyyy-MM-dd'T'HH:mm:ss'Z'"),
            ];
            for (const formatter of formatters) {
                try {
                    return LocalDate.parse(data, formatter).format(DateTimeFormatter.ofPattern('yyyy-MM-dd'));
                } catch {
                    // Ignore and try the next formatter
                }
            }
            return data;
        }
        case 'datetime-local':
            try {
                return Instant.parse(data)
                    .atZone(ZoneId.of('UTC'))
                    .format(DateTimeFormatter.ofPattern("yyyy-MM-dd'T'HH:mm"));
            } catch {
                return '';
            }
        default:
            return data;
    }
};

export const InputControlComponent = ({
    id,
    path,
    data,
    handleChange,
    description,
    errors,
    label,
    required,
    visible,
    enabled,
    onFocus,
    type = 'text',
}: ControlProps & InputProps) => {
    const hasErrors = errors.length > 0;

    const formattedData = useMemo(() => formatData(data, type, enabled), [data, enabled, type]);

    // For number inputs, maintain internal state to preserve display value (e.g., "1.50")
    // while sending the actual number (1.5) to the form
    const [displayValue, setDisplayValue] = useState<string>(() => {
        if (type === 'number') {
            return formattedData === 0 ? '0' : String(formattedData || '');
        }
        return '';
    });

    // Track the last value we sent to the form to detect external changes
    const lastSentValue = useRef<any>(data);

    // Sync display value with external data changes (but not internal changes)
    useEffect(() => {
        if (type === 'number') {
            // Only update display value if data changed from an external source
            // (not from our own handleChange call)
            if (data !== lastSentValue.current) {
                setDisplayValue(String(formattedData === 0 ? 0 : formattedData || ''));
            }
        }
    }, [data, formattedData, type]);

    if (fakeRequired.includes(path)) {
        required = true; // make it look like required
    }

    const isLight = lightStyle.includes(path);

    const variant = hasErrors ? (isLight ? 'errorLight' : 'errorDefault') : isLight ? 'light' : 'default';

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;

        if (type === 'number') {
            // Update display value immediately for smooth typing
            setDisplayValue(newValue);

            // Format the value (may return string or number)
            const formattedValue = formatUpdateValue(newValue, type);

            // Convert to number for form validation only if it's a valid complete number
            // Keep incomplete inputs (empty, ".", "-", "1.50", etc.) as strings
            let valueForForm = formattedValue;
            if (typeof formattedValue === 'string' && formattedValue !== '') {
                const numValue = Number(formattedValue.replace(/_/g, ''));
                // Only convert if it's a valid finite number AND not NaN
                if (Number.isFinite(numValue)) {
                    valueForForm = numValue;
                }
            }

            // Track the value we're sending to prevent external sync
            lastSentValue.current = valueForForm;

            // Send the value to the form
            handleChange(path, valueForForm);
        } else {
            handleChange(path, formatUpdateValue(newValue, type));
        }
    };

    // Use internal display value for number inputs, formatted data for others
    const inputValue = type === 'number' ? displayValue : formattedData;

    return visible ? (
        <div>
            <div className="flex items-center gap-2">
                <Label label={label} required={required} htmlFor={id} />
                <Description description={description} />
            </div>
            <JsonFormErrorFloatingPane hasErrors={hasErrors} errors={<Errors errors={errors} />}>
                <Input
                    id={id}
                    type={type}
                    value={inputValue}
                    disabled={!enabled}
                    dimensions="sm"
                    variant={variant}
                    onChange={handleInputChange}
                    onFocus={onFocus}
                />
            </JsonFormErrorFloatingPane>
        </div>
    ) : null;
};

export const InputControlTester = rankWith(
    3, //increase rank as needed
    isStringControl,
);

export const InputControl = withJsonFormsControlProps(InputControlComponent);
